import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Send, Check } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import { EmptyState, ErrorState } from "../../components/ui/Feedback";
import {
  FormField,
  SegmentedControl,
  fieldA11y,
} from "../../components/ui/FormControls";
import {
  parseLocalDate,
  toInputValue,
  todayInputValue,
  formatDateGB,
  daysInclusive,
} from "./leaveUtils";
import "./ApplyLeave.scss";

const API = process.env.REACT_APP_API_EMPLOYEE_URL;

const PREDEFINED_REASONS = [
  "Family Emergency",
  "Personal Work",
  "Vacation",
  "I am sick!",
  "Doctor's Appointment",
  "Child Care",
  "Car Trouble",
  "Weather Issues",
  "Bereavement",
  "Mental Health Day",
  "Relocation",
  "Unexpected Travel",
  "Public Transportation Delays",
  "Study or Exam",
  "Pet Emergency",
  "Meeting with Authorities",
  "Health Check-up",
];
const VISIBLE_REASONS = 8;

const DURATION_OPTIONS = [
  { value: "full", label: "Full Day" },
  { value: "first_half", label: "First Half" },
  { value: "second_half", label: "Second Half" },
];
const DURATION_LABEL = {
  full: "Full Day",
  first_half: "First Half",
  second_half: "Second Half",
};
const DURATION_ENUM = {
  full: "FULL_DAY",
  first_half: "FIRST_HALF",
  second_half: "SECOND_HALF",
};

const STATUS_MESSAGES = {
  400: "Bad Request. Please check your input.",
  401: "Unauthorized. Please log in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource could not be found.",
  409: "Leave already exists for the selected dates.",
  500: "Server error. Please try again later.",
};

const EMPTY_FORM = {
  leaveType: "",
  leaveTypeId: null,
  // "YYYY-MM-DD" strings (what <input type="date"> produces)
  dateRange: [null, null],
  duration: "full",
  reason: "",
  // full employee object, so we can send its empId
  fallbackEmployee: null,
  responsibility: "",
};

// Which element to focus when validation fails, in on-screen order.
const ERROR_ORDER = [
  "leaveType",
  "dateRange",
  "fallbackEmployee",
  "responsibility",
  "reason",
];

const formatDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const ApplyLeave = () => {
  const { user } = useAuth();

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [tableData, setTableData] = useState([]); // balance per leave type
  const [fallbackEmployees, setFallbackEmployees] = useState([]);
  const [optionalHolidays, setOptionalHolidays] = useState([]);
  const [selectedOptionalHoliday, setSelectedOptionalHoliday] = useState(null);
  const [isOptionalHolidayType, setIsOptionalHolidayType] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showAllReasons, setShowAllReasons] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // ---- data loading ---------------------------------------------------------
  // One request supplies both the leave types and the per-type balances
  // (the old code requested /Leave-Types twice).
  const fetchLeaveData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setLoadError(false);
    }
    try {
      const response = await axiosInstance.get(`${API}/Leave-Types`);
      const data = response.data.data;
      const list = Array.isArray(data) ? data : [];
      setLeaveTypes(list);
      setTableData(
        list.map((leave) => ({
          category: leave.leaveTypeName,
          totalLeaves: leave.totalLeaves,
          usedLeaves: leave.usedLeaves,
          remainingLeaves: leave.availableLeaves,
        })),
      );
    } catch (error) {
      console.error("Error fetching leave data:", error);
      if (!silent) {
        setLeaveTypes([]);
        setTableData([]);
        setLoadError(true);
        toast.error("Failed to load leave data. Please refresh the page.");
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveData();
  }, [fetchLeaveData]);

  useEffect(() => {
    const fetchFallbackEmployees = async () => {
      try {
        const response = await axiosInstance.get(`${API}/employees/All`);
        const { data } = response.data;
        setFallbackEmployees(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching fallback employees:", error);
        setFallbackEmployees([]);
      }
    };
    fetchFallbackEmployees();
  }, []);

  const fetchOptionalHolidays = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${API}/holidays/optional/all`);
      const { data } = response.data;
      setOptionalHolidays(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching optional holidays:", error);
      setOptionalHolidays([]);
    }
  }, []);

  // ---- leave type selection ---------------------------------------------------
  const applyLeaveType = useCallback(
    (name) => {
      const selected = leaveTypes.find((type) => type.leaveTypeName === name);
      const isOptional = selected?.optionalHoliday === true;

      setFormData((prev) => ({
        ...prev,
        leaveType: name,
        leaveTypeId: selected?.leaveTypeId ?? null,
        // Only reset dates when switching between optional / regular modes.
        dateRange:
          isOptional !== isOptionalHolidayType ? [null, null] : prev.dateRange,
      }));
      setIsOptionalHolidayType(isOptional);
      setSelectedOptionalHoliday(null);
      if (isOptional) fetchOptionalHolidays();
    },
    [leaveTypes, isOptionalHolidayType, fetchOptionalHolidays],
  );

  // Pre-select the first leave type that still has balance (also runs again
  // after a successful submit resets the form).
  useEffect(() => {
    if (tableData.length === 0 || formData.leaveType) return;
    const firstAvailable = tableData.find((item) => item.remainingLeaves > 0);
    if (firstAvailable) applyLeaveType(firstAvailable.category);
  }, [tableData, formData.leaveType, applyLeaveType]);

  // ---- field handlers ----------------------------------------------------------
  const clearError = (name) => {
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleDurationChange = (value) => {
    setFormData((prev) => ({ ...prev, duration: value }));
    clearError("duration");
  };

  const handleLeaveTypeClick = (item) => {
    if (!(item.remainingLeaves > 0)) return;
    applyLeaveType(item.category);
    clearError("leaveType");
  };

  const handleFallbackChange = (e) => {
    const empId = parseInt(e.target.value, 10);
    const selectedEmployee =
      fallbackEmployees.find((emp) => emp.empId === empId) || null;
    setFormData((prev) => ({ ...prev, fallbackEmployee: selectedEmployee }));
    clearError("fallbackEmployee");
  };

  const handleReasonPick = (reason) => {
    setFormData((prev) => ({ ...prev, reason }));
    clearError("reason");
  };

  const handleOptionalHolidayChange = (e) => {
    const holidayId = parseInt(e.target.value, 10);
    const holiday =
      optionalHolidays.find((h) => h.holidayId === holidayId) || null;
    setSelectedOptionalHoliday(holiday);

    if (holiday) {
      const day = toInputValue(parseLocalDate(holiday.eventDate));
      setFormData((prev) => ({ ...prev, dateRange: [day, day] }));
    } else {
      setFormData((prev) => ({ ...prev, dateRange: [null, null] }));
    }
    clearError("dateRange");
  };

  const handleStartDateChange = (e) => {
    const start = e.target.value || null;
    setFormData((prev) => {
      const end = prev.dateRange[1];
      return {
        ...prev,
        dateRange: [start, end && start && end < start ? null : end],
      };
    });
    clearError("dateRange");
  };

  const handleEndDateChange = (e) => {
    const end = e.target.value || null;
    setFormData((prev) => ({ ...prev, dateRange: [prev.dateRange[0], end] }));
    clearError("dateRange");
  };

  // ---- the request, computed once and used by BOTH the summary and submit -----
  const computeRequest = () => {
    if (isOptionalHolidayType && selectedOptionalHoliday) {
      const base = selectedOptionalHoliday.noOfdays || 1;
      return {
        leaveDates: [selectedOptionalHoliday.eventDate],
        noOfDays: formData.duration === "full" ? base : base * 0.5,
      };
    }

    const [from, to] = formData.dateRange;
    const daysDiff = daysInclusive(from, to);
    if (daysDiff === 0) return { leaveDates: [], noOfDays: 0 };

    const leaveDates = [];
    const cursor = parseLocalDate(from);
    const end = parseLocalDate(to);
    while (cursor <= end) {
      leaveDates.push(toInputValue(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return {
      leaveDates,
      noOfDays: formData.duration === "full" ? daysDiff : daysDiff * 0.5,
    };
  };

  // ---- validation & submit -----------------------------------------------------
  const validateForm = () => {
    const newErrors = {};
    if (!formData.leaveType) newErrors.leaveType = "Leave Type is required";
    if (!formData.duration) newErrors.duration = "Duration is required";
    if (!formData.reason.trim()) newErrors.reason = "Reason is required";
    if (!formData.responsibility.trim()) {
      newErrors.responsibility = "Responsibility is required";
    }
    if (!formData.fallbackEmployee) {
      newErrors.fallbackEmployee = "Fallback Employee is required";
    }
    if (!formData.dateRange[0] || !formData.dateRange[1]) {
      newErrors.dateRange = "Please select a valid date range";
    }
    setErrors(newErrors);
    return newErrors;
  };

  const focusFirstError = (newErrors) => {
    const firstKey = ERROR_ORDER.find((key) => newErrors[key]);
    const ids = {
      leaveType: "leave-type-group",
      dateRange: isOptionalHolidayType ? "optionalHoliday" : "startDate",
      fallbackEmployee: "fallback",
      responsibility: "responsibility",
      reason: "reason",
    };
    const el = firstKey && document.getElementById(ids[firstKey]);
    if (el) el.focus();
  };

  const resetForm = () => {
    // The pre-select effect then picks the first available leave type again.
    setFormData(EMPTY_FORM);
    setErrors({});
    setIsOptionalHolidayType(false);
    setSelectedOptionalHoliday(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // no double submits

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");
      focusFirstError(newErrors);
      return;
    }

    const { leaveDates, noOfDays } = computeRequest();
    const requestBody = {
      leaveId: 0,
      leaveDuration: DURATION_ENUM[formData.duration],
      noOfDays,
      comments: formData.reason,
      responsibility: formData.responsibility,
      leaveDates,
      fallBackEmployeeId: formData.fallbackEmployee?.empId ?? 0,
      leaveTypeId: formData.leaveTypeId,
    };

    setSubmitting(true);
    try {
      await axiosInstance.post(`${API}/leave-history`, requestBody);
      toast.success("Leave request submitted successfully!");
      resetForm();
      // Balances changed; refresh them without flashing the loading state.
      fetchLeaveData({ silent: true });
    } catch (error) {
      console.error("Error submitting leave request:", error);
      const message = error.response
        ? STATUS_MESSAGES[error.response.status] ||
          "Failed to submit leave request. Please try again."
        : "Network error. Please check your connection and try again.";
      toast.error(message);
      // Form state is intentionally kept so the user can fix and retry.
    } finally {
      setSubmitting(false);
    }
  };

  // ---- derived values for the summary panel --------------------------------------
  const { noOfDays: requestedDays } = computeRequest();
  const selectedBalance = tableData.find(
    (item) => item.category === formData.leaveType,
  );
  const balanceAfter =
    selectedBalance &&
    requestedDays > 0 &&
    Number.isFinite(Number(selectedBalance.remainingLeaves))
      ? Number(selectedBalance.remainingLeaves) - requestedDays
      : null;

  const [fromDate, toDate] = formData.dateRange;
  let datesSummary = "Not selected";
  if (isOptionalHolidayType && selectedOptionalHoliday) {
    datesSummary = `${selectedOptionalHoliday.holidayName} · ${formatDateGB(
      selectedOptionalHoliday.eventDate,
    )}`;
  } else if (fromDate && toDate) {
    datesSummary =
      fromDate === toDate
        ? formatDateGB(fromDate)
        : `${formatDateGB(fromDate)} – ${formatDateGB(toDate)}`;
  }

  const filteredEmployees = fallbackEmployees.filter(
    (emp) =>
      emp.companyEmail?.toLowerCase().trim() !==
      user?.username?.toLowerCase().trim(),
  );

  const reasonsToShow = showAllReasons
    ? PREDEFINED_REASONS
    : PREDEFINED_REASONS.slice(0, VISIBLE_REASONS);
  // Keep the chosen reason visible even if it's in the collapsed part.
  if (
    !showAllReasons &&
    PREDEFINED_REASONS.includes(formData.reason) &&
    !reasonsToShow.includes(formData.reason)
  ) {
    reasonsToShow.push(formData.reason);
  }

  // ---- loading / error --------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="apply-leave" aria-busy="true">
        <section className="hr-card apply-card">
          <span className="hr-skeleton apply-skeleton-title" />
          <div className="leave-cards">
            {Array.from({ length: 4 }, (_, i) => (
              <span key={i} className="hr-skeleton apply-skeleton-card" />
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="apply-leave">
        <section className="hr-card">
          <ErrorState
            title="Couldn't load your leave balances"
            message="Check your connection and try again."
            onRetry={() => fetchLeaveData()}
          />
        </section>
      </div>
    );
  }

  return (
    <div className="apply-leave">
      <form
        id="apply-leave-form"
        className="apply-leave__layout"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="apply-leave__main">
          {/* ---------------- 1. Leave type ---------------- */}
          <section
            className="hr-card apply-card"
            aria-labelledby="leave-type-label"
          >
            <div className="apply-card__heading">
              <h2 id="leave-type-label">Select your balance</h2>
              <Link to="/ess/my-leave" className="hr-btn hr-btn--secondary">
                View my leave
              </Link>
            </div>

            {tableData.length === 0 ? (
              <EmptyState
                title="No leave balances available"
                description="Your leave types will appear here once they're set up."
              />
            ) : (
              <div
                id="leave-type-group"
                className="leave-cards"
                role="radiogroup"
                aria-labelledby="leave-type-label"
                tabIndex={-1}
              >
                {tableData.map((item, index) => {
                  const exhausted = item.remainingLeaves <= 0;
                  const selected = formData.leaveType === item.category;
                  const usedPct =
                    item.totalLeaves > 0
                      ? Math.min(
                          100,
                          Math.max(
                            0,
                            (item.usedLeaves / item.totalLeaves) * 100,
                          ),
                        )
                      : 0;

                  return (
                    <button
                      key={`${item.category}-${index}`}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={exhausted}
                      className={`leave-card ${selected ? "is-selected" : ""}`}
                      onClick={() => handleLeaveTypeClick(item)}
                    >
                      {selected && (
                        <span className="leave-card__check" aria-hidden="true">
                          <Check size={13} strokeWidth={3} />
                        </span>
                      )}
                      <span className="leave-card__name">{item.category}</span>
                      <span className="leave-card__remaining">
                        {item.remainingLeaves}
                        <small>left</small>
                      </span>
                      <span className="leave-card__meta">
                        {item.usedLeaves} used of {item.totalLeaves}
                      </span>
                      <span className="leave-card__bar" aria-hidden="true">
                        <span style={{ width: `${usedPct}%` }} />
                      </span>
                      {exhausted && (
                        <span className="leave-card__badge">
                          No leaves remaining
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {errors.leaveType && (
              <p className="hr-form-error apply-card__error" role="alert">
                {errors.leaveType}
              </p>
            )}
          </section>

          {/* ---------------- 2. Details ---------------- */}
          <section
            className="hr-card apply-card"
            aria-labelledby="details-heading"
          >
            <h2 id="details-heading">Leave details</h2>
            <p className="apply-card__desc">
              Tell us when you'll be away and who covers for you.
            </p>

            <div className="apply-form-grid">
              <FormField
                id="duration"
                label="Duration"
                labelAs="span"
                labelId="duration-label"
                error={errors.duration}
              >
                <SegmentedControl
                  id="duration"
                  labelledBy="duration-label"
                  options={DURATION_OPTIONS}
                  value={formData.duration}
                  onChange={handleDurationChange}
                />
              </FormField>

              {isOptionalHolidayType ? (
                <FormField
                  id="optionalHoliday"
                  label="Optional holiday"
                  required
                  error={errors.dateRange}
                  className="apply-form-grid__optional-holiday"
                >
                  <select
                    {...fieldA11y("optionalHoliday", {
                      error: errors.dateRange,
                    })}
                    className="hr-select__control"
                    value={selectedOptionalHoliday?.holidayId ?? ""}
                    onChange={handleOptionalHolidayChange}
                  >
                    <option value="">Select an optional holiday</option>
                    {optionalHolidays.map((holiday) => (
                      <option key={holiday.holidayId} value={holiday.holidayId}>
                        {holiday.holidayName} -{" "}
                        {formatDateGB(holiday.eventDate)}
                      </option>
                    ))}
                  </select>
                </FormField>
              ) : (
                <div className="apply-form-grid__dates">
                  <FormField id="startDate" label="From date" required>
                    <input
                      {...fieldA11y("startDate", { error: errors.dateRange })}
                      type="date"
                      className="hr-input"
                      value={formData.dateRange[0] || ""}
                      min={todayInputValue()}
                      onChange={handleStartDateChange}
                    />
                  </FormField>
                  <FormField
                    id="endDate"
                    label="To date"
                    required
                    error={errors.dateRange}
                  >
                    <input
                      {...fieldA11y("endDate", { error: errors.dateRange })}
                      type="date"
                      className="hr-input"
                      value={formData.dateRange[1] || ""}
                      min={formData.dateRange[0] || todayInputValue()}
                      onChange={handleEndDateChange}
                    />
                  </FormField>
                </div>
              )}

              <FormField
                id="fallback"
                label="Fallback employee"
                required
                error={errors.fallbackEmployee}
              >
                <select
                  {...fieldA11y("fallback", { error: errors.fallbackEmployee })}
                  className="hr-select__control"
                  value={formData.fallbackEmployee?.empId ?? ""}
                  onChange={handleFallbackChange}
                >
                  <option value="">Select a fallback person</option>
                  {filteredEmployees
                    .filter((emp) => emp.email !== user?.username)
                    .map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                </select>
              </FormField>

              <FormField
                id="reason"
                label="Reason"
                required
                error={errors.reason}
                className="apply-form-grid__half"
              >
                <input
                  {...fieldA11y("reason", { error: errors.reason })}
                  name="reason"
                  type="text"
                  className="hr-input"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Choose or type your reason for leave"
                  autoComplete="off"
                />
                <div
                  className="reason-chips"
                  role="group"
                  aria-label="Common reasons"
                >
                  {reasonsToShow.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className={`hr-pill-btn ${
                        formData.reason === reason ? "is-active" : ""
                      }`}
                      aria-pressed={formData.reason === reason}
                      onClick={() => handleReasonPick(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="hr-link-btn"
                    aria-expanded={showAllReasons}
                    onClick={() => setShowAllReasons((prev) => !prev)}
                  >
                    {showAllReasons
                      ? "Show fewer"
                      : `Show all ${PREDEFINED_REASONS.length}`}
                  </button>
                </div>
              </FormField>

              <FormField
                id="responsibility"
                label="Responsibility"
                required
                error={errors.responsibility}
                className="apply-form-grid__half"
              >
                <textarea
                  {...fieldA11y("responsibility", {
                    error: errors.responsibility,
                  })}
                  name="responsibility"
                  className="hr-textarea"
                  value={formData.responsibility}
                  onChange={handleChange}
                  placeholder="Enter your current responsibilities"
                />
              </FormField>
            </div>
          </section>
        </div>
      </form>
      <div className="apply-leave__action-bar">
        <div className="apply-leave__action-details" aria-live="polite">
          <div className="apply-leave__action-detail">
            <span>Leave type</span>
            <strong>{formData.leaveType || "Choose a type"}</strong>
          </div>
          <div className="apply-leave__action-detail">
            <span>Duration</span>
            <strong>{DURATION_LABEL[formData.duration]}</strong>
          </div>
          <div className="apply-leave__action-detail">
            <span>Dates</span>
            <strong>{datesSummary}</strong>
          </div>
          <div className="apply-leave__action-detail">
            <span>Fallback</span>
            <strong title="Your fallback employee will be asked to confirm they can cover.">
              {formData.fallbackEmployee
                ? `${formData.fallbackEmployee.firstName} ${formData.fallbackEmployee.lastName}`
                : "Not selected"}
            </strong>
          </div>
          <div className="apply-leave__action-detail">
            <span>Days requested</span>
            <strong>{formatDays(requestedDays)}</strong>
          </div>
          {balanceAfter !== null && (
            <div className="apply-leave__action-detail">
              <span>Balance after</span>
              <strong className={balanceAfter < 0 ? "is-negative" : undefined}>
                {formatDays(balanceAfter)}
              </strong>
              {balanceAfter < 0 && (
                <small role="status">Over available balance</small>
              )}
            </div>
          )}
        </div>
        <button
          type="submit"
          form="apply-leave-form"
          className="hr-btn hr-btn--primary hr-btn--lg"
          disabled={submitting}
        >
          <Send size={16} aria-hidden="true" />
          {submitting ? "Submitting…" : "Apply for leave"}
        </button>
      </div>
    </div>
  );
};

export default ApplyLeave;
